import {
  clampVolume,
  defaultVolumes,
  effectiveVolume,
  type AudioChannel,
  type ChannelVolumes,
  type PlayableChannel,
} from './channels';

export interface AudioAsset {
  /** Nombre con el que se pide el sonido, por ejemplo `door-open`. */
  id: string;
  /** Fuentes en orden de preferencia; Howler elige el primer formato admitido. */
  sources: string[];
  /** Se repite hasta detenerlo. Propio de música y ambiente. */
  loop?: boolean;
  /**
   * Formatos de la fuente. Solo hace falta cuando la dirección no lo delata,
   * como en un `blob:` servido desde la caché del navegador.
   */
  format?: string[];
  /**
   * Reproducir con un `<audio>` en vez de Web Audio.
   *
   * Web Audio descarga el archivo por su cuenta y necesita que el servidor
   * permita CORS. Un `<audio>` no lo necesita, así que es la única forma de
   * reproducir un archivo de un origen que no lo declara, como R2 con URL
   * firmada.
   */
  html5?: boolean;
  /** Multiplicador propio del evento, aplicado sobre el volumen del canal. */
  volume?: number;
}

export interface AudioEngineOptions {
  volumes?: Partial<ChannelVolumes>;
  /** Inyectable para pruebas; por defecto construye un `Howl` real. */
  createSound?: (asset: AudioAsset, volume: number) => AudioHandle | Promise<AudioHandle>;
  /**
   * Además de `console.error`, algo se rompió al preparar o reproducir un
   * sonido. `engine/` no puede llamar a `reportClientError` por su cuenta
   * (no importa nada de `src/shared` ni `src/services` — ver AGENTS.md), así
   * que quien construye el motor decide qué hacer con el aviso; sin este
   * callback el error sigue solo en consola, como antes.
   */
  onError?: (message: string, details: unknown) => void;
}

/** Lo mínimo que el motor necesita de una pista. Howl lo cumple. */
export interface AudioHandle {
  play(): void;
  /** Se resuelve cuando la reproducción iniciada termina o falla. */
  waitForEnd?(): Promise<void>;
  stop(): void;
  volume(value: number): void;
  unload(): void;
  /**
   * Pausar y reanudar son opcionales: una pista que no sabe hacerlo se
   * detiene y vuelve a empezar, que es lo que hace un efecto corto. Para la
   * música sí importa, y Howler las implementa.
   */
  pause?(): void;
  resume?(): void;
}

interface Track {
  channel: PlayableChannel;
  asset: AudioAsset;
  handle: AudioHandle;
}

type HowlerModule = typeof import('howler');
let howlerModule: HowlerModule | null = null;
let howlerModulePromise: Promise<HowlerModule> | null = null;

/**
 * Trae Howler recién cuando suena el primer sonido, no al abrir la app: así
 * ese paquete no forma parte de lo que hay que descargar antes de ver la
 * primera pantalla, que casi nunca reproduce audio.
 */
function loadHowler(): Promise<HowlerModule> {
  howlerModulePromise ??= import('howler').then((module) => (howlerModule = module));
  return howlerModulePromise;
}

async function howlFactory(
  asset: AudioAsset,
  volume: number,
  onError?: AudioEngineOptions['onError'],
): Promise<AudioHandle> {
  const { Howl } = await loadHowler();
  const howl = new Howl({
    src: asset.sources,
    loop: asset.loop === true,
    volume,
    ...(asset.format ? { format: asset.format } : {}),
    ...(asset.html5 ? { html5: true } : {}),
    onloaderror: (_soundId, error) => {
      const message = `No se pudo descargar el sonido: ${asset.id}`;
      console.error(`[Rol Sensorial] ${message}`, error);
      onError?.(message, error);
    },
    onplayerror: (_soundId, error) => {
      const message = `El navegador rechazó el sonido: ${asset.id}`;
      console.error(`[Rol Sensorial] ${message}`, error);
      onError?.(message, error);
    },
  });
  let completion: Promise<void> = Promise.resolve();
  return {
    play: () => {
      completion = new Promise<void>((resolve) => {
        let settled = false;
        const done = () => {
          if (settled) return;
          settled = true;
          howl.off('end', done);
          howl.off('stop', done);
          howl.off('loaderror', done);
          howl.off('playerror', done);
          resolve();
        };
        howl.once('end', done);
        howl.once('stop', done);
        howl.once('loaderror', done);
        howl.once('playerror', done);
        void howl.play();
      });
    },
    waitForEnd: () => completion,
    stop: () => howl.stop(),
    volume: (value: number) => void howl.volume(value),
    unload: () => howl.unload(),
    pause: () => void howl.pause(),
    resume: () => void howl.play(),
  };
}

/**
 * Motor de audio del juego. No conoce React ni el DOM de la aplicación: recibe
 * assets y órdenes, y mantiene el estado de cada canal.
 *
 * No reproduce nada por su cuenta. Cualquier sonido nace de una acción de la
 * persona o de un evento del juego que ella provocó, nunca de la carga de una
 * pantalla.
 */
export class AudioEngine {
  private readonly volumes: ChannelVolumes;
  private readonly createSound: (asset: AudioAsset, volume: number) => AudioHandle | Promise<AudioHandle>;
  private readonly catalog = new Map<string, AudioAsset>();
  private readonly playing = new Map<string, Track>();
  /** Última pista pedida por canal exclusivo, para descartar una que tardó en cargar si ya no es la vigente. */
  private readonly generation = new Map<PlayableChannel, number>();
  private readonly onError?: AudioEngineOptions['onError'];
  private muted = false;

  constructor(options: AudioEngineOptions = {}) {
    this.volumes = { ...defaultVolumes, ...options.volumes };
    this.onError = options.onError;
    this.createSound = options.createSound ?? ((asset, volume) => howlFactory(asset, volume, this.onError));
  }

  /**
   * Prepara el desbloqueo de audio antes de la primera acción de la persona.
   *
   * Es opt-in: el proveedor no lo llama al montar la aplicación. La primera
   * reproducción carga Howler por `import()` y los consumidores que necesiten
   * adelantar el desbloqueo pueden invocar este método desde una interacción
   * del usuario.
   */
  async prepare(): Promise<void> {
    if (this.createSound !== howlFactory) return;
    const { Howler } = await loadHowler();
    const unlockable = Howler as typeof Howler & { _unlockAudio?: () => void };
    unlockable._unlockAudio?.();
  }

  /** Declara los sonidos disponibles. Un juego registra los suyos al cargarse. */
  register(assets: AudioAsset[]): void {
    for (const asset of assets) this.catalog.set(asset.id, asset);
  }

  getVolume(channel: AudioChannel): number {
    return this.volumes[channel];
  }

  getVolumes(): ChannelVolumes {
    return { ...this.volumes };
  }

  setVolume(channel: AudioChannel, value: number): void {
    this.volumes[channel] = clampVolume(value);
    this.applyVolumes();
  }

  isMuted(): boolean {
    return this.muted;
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    this.applyVolumes();
  }

  playMusic(id: string): Promise<void> {
    return this.playOn('music', id, true);
  }

  playAmbient(id: string): Promise<void> {
    return this.playOn('ambient', id, true);
  }

  playEffect(id: string): Promise<void> {
    return this.playOn('effects', id, false);
  }

  playVoice(id: string): Promise<void> {
    return this.playOn('voice', id, true);
  }

  stopMusic(): void {
    this.stopChannel('music');
  }

  stopAmbient(): void {
    this.stopChannel('ambient');
  }

  stopChannel(channel: PlayableChannel): void {
    for (const [key, track] of this.playing) {
      if (track.channel !== channel) continue;
      track.handle.stop();
      track.handle.unload();
      this.playing.delete(key);
    }
  }

  /**
   * Pausa lo que suena en un canal sin descargarlo, para poder retomarlo.
   *
   * Una pista que no sabe pausarse se detiene: es lo correcto para un efecto
   * corto, donde retomar a mitad no significa nada.
   */
  pauseChannel(channel: PlayableChannel): void {
    for (const track of this.playing.values()) {
      if (track.channel !== channel) continue;
      if (track.handle.pause) track.handle.pause();
      else track.handle.stop();
    }
  }

  /** Retoma lo que se pausó en un canal. */
  resumeChannel(channel: PlayableChannel): void {
    for (const track of this.playing.values()) {
      if (track.channel !== channel) continue;
      if (track.handle.resume) track.handle.resume();
      else track.handle.play();
    }
  }

  stopAll(): void {
    for (const [key, track] of this.playing) {
      track.handle.stop();
      track.handle.unload();
      this.playing.delete(key);
    }
  }

  /** Libera todo. Se llama al desmontar la aplicación o cambiar de juego. */
  dispose(): void {
    this.stopAll();
    this.catalog.clear();
  }

  /**
   * `exclusive` describe los canales donde solo tiene sentido una pista a la
   * vez: dos músicas simultáneas se pisan, dos efectos no.
   *
   * `createSound` puede tardar (la primera vez, carga Howler de la red), así
   * que puede devolver una promesa. Si mientras tanto llega una pista
   * exclusiva más nueva para el mismo canal, la que termina de cargar tarde
   * se descarta en vez de sonar pisando a la vigente.
   */
  private playOn(channel: PlayableChannel, id: string, exclusive: boolean): Promise<void> {
    const asset = this.catalog.get(id);
    if (!asset) {
      console.error(`[Rol Sensorial] Sonido no registrado: ${id}`);
      this.onError?.(`Sonido no registrado: ${id}`, null);
      return Promise.resolve();
    }
    if (exclusive) this.stopChannel(channel);

    const key = exclusive ? `${channel}:${id}` : `${channel}:${id}:${crypto.randomUUID()}`;
    const token = exclusive ? (this.generation.get(channel) ?? 0) + 1 : 0;
    if (exclusive) this.generation.set(channel, token);

    const attach = (handle: AudioHandle): Promise<void> => {
      if (exclusive && this.generation.get(channel) !== token) {
        handle.stop();
        handle.unload();
        return Promise.resolve();
      }
      this.playing.set(key, { channel, asset, handle });
      handle.play();
      return handle.waitForEnd?.() ?? Promise.resolve();
    };

    try {
      const volume = this.volumeFor(channel, asset);
      if (volume === 0) {
        console.warn(
          `[Rol Sensorial] Sonido en volumen cero: ${id}; `
          + `silenciado: ${this.muted}; maestro: ${this.volumes.master}; `
          + `${channel}: ${this.volumes[channel]}`,
        );
      }
      const result = this.createSound(asset, volume);
      if (result instanceof Promise) {
        return result.then(attach).catch((caught: unknown) => {
          console.error(`[Rol Sensorial] No se pudo preparar el sonido: ${id}`, caught);
          this.onError?.(`No se pudo preparar el sonido: ${id}`, caught);
        });
      }
      return attach(result);
    } catch (caught) {
      console.error(`[Rol Sensorial] No se pudo preparar el sonido: ${id}`, caught);
      this.onError?.(`No se pudo preparar el sonido: ${id}`, caught);
      return Promise.resolve();
    }
  }

  private volumeFor(channel: PlayableChannel, asset?: AudioAsset): number {
    const eventVolume = asset?.volume ?? 1;
    return this.muted ? 0 : effectiveVolume(this.volumes, channel) * Math.max(0, Math.min(1, eventVolume));
  }

  private applyVolumes(): void {
    for (const track of this.playing.values()) {
      track.handle.volume(this.volumeFor(track.channel, track.asset));
    }
    // Si Howler todavía no se cargó, no hay nada sonando de verdad: el
    // volumen ya queda bien aplicado en cuanto se cree la primera pista, más
    // arriba. No hace falta traer el paquete solo para este mute global.
    howlerModule?.Howler.mute(this.muted);
  }
}
